import { FC } from 'react';
import { usePostHog } from '@posthog/react';
import { useTranslation } from 'react-i18next';
import { Button, Alert } from 'react-daisyui';
import Modal, { ModalProps } from '@/components/modals/Modal/Modal.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import useReactivateSubscription from '@/api/stripe/useReactivateSubscription.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';

type SubscriptionReactivateModalProps = ModalProps & {
  setIsCancelAtPeriodEnd: (value: boolean, action: () => Promise<void>) => void;
};

const SubscriptionReactivateModal: FC<SubscriptionReactivateModalProps> = ({
  onClose,
  setIsCancelAtPeriodEnd,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { subscription } = useAuthContext();
  const { mutateAsync: reactivateSubscription, isPending: isLoading, error } = useReactivateSubscription();

  const handleConfirm = async () => {
    try {
      if (!subscription?.subscription_id) return;
      await reactivateSubscription({
        subscriptionId: subscription.subscription_id!,
      });
      posthog?.capture('subscription_reactivated');
      setIsCancelAtPeriodEnd(true, async () => {
        return Promise.resolve();
      });
      onClose?.();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal.Wrapper wrapperClassName="w-[400px]" {...modalProps} onClose={onClose} showCloseButton={false} closeable>
      <Modal.Title>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="font-feature font-bold italic text-2xl">{t('subscriptionReactivateModal.title')}</h2>
          <p className="text-sm text-text-secondary">{t('subscriptionReactivateModal.subtitle')}</p>
        </div>
      </Modal.Title>
      <div className="flex flex-col gap-3">
        {error ? <Alert status="error">{t('subscriptionReactivateModal.error')}</Alert> : null}
      </div>
      <Modal.Nav>
        {({ handleClose }) => (
          <div className="flex flex-col gap-4 w-full">
            <LoadingButton loading={isLoading} type="button" color="primary" className="w-full" onClick={handleConfirm}>
              {t('button.reactivate')}
            </LoadingButton>
            <Button type="button" color="ghost" className="text-primary" onClick={handleClose}>
              {t('button.cancel')}
            </Button>
          </div>
        )}
      </Modal.Nav>
    </Modal.Wrapper>
  );
};

export default SubscriptionReactivateModal;
